import React, { useState, Fragment } from "react";
import { useFormikContext } from "formik";
import get from "lodash/get";
import { COLORS } from "utils/styles";
import HoverIconButton from "components/HoverIconButton";
import ConfirmIconButton from "components/ConfirmIconButton";
import FieldError from "components/FieldError";
import classnames from "classnames";
import { PRODUCT_PRICE_REMOVAL } from "utils/constants/confirm";
import { getPricePickerPriceLabel, priceIntervalCountLabel } from "utils/price";
import CreatePriceForm from "./CreatePriceForm";
import Overlay from "components/Overlay";
import SectionBezel from "components/SectionBezel";
import Button from "components/Form/fields/Button";
import { flattenErrorKeys } from "utils/form";
import { getPriceTaxBehaviorLabel } from "utils/taxRate";

const EditPriceForm = ({ name }) => {
  <div className="f5">{`Edit form for ${name}`}</div>;
};

/**
 * Select a price when in the context of a product
 */
const CheckoutPricePicker = ({ name, arrayHelpers, modelIx, showOnMount }) => {
  const [showOverlay, setShowOverlay] = useState(showOnMount);
  const { values, touched, setFieldTouched, errors } = useFormikContext();
  const editMode = !Boolean(values.id);

  const model = get(values, name);

  const content = model.id ? (
    <EditPriceForm name={name} />
  ) : (
    <CreatePriceForm name={name} />
  );
  const taxBehavior = getPriceTaxBehaviorLabel(model);
  const hasFooter = model.nickname || taxBehavior;

  const activePriceInfo = model ? (
    <div className="flex flex-column flex-row-ns w-100 items-center-ns">
      <div className="flex flex-row">
        <div className="fw5 f5 pl0 pr2 ph2-ns bl-ns hairline-1">
          {model.currency.toUpperCase()}
        </div>
        <div
          className={classnames("fw5 f5 pl2 bl hairline-1", {
            pr2: model.nickname
          })}
        >
          {`${priceIntervalCountLabel(model)}`}
        </div>
      </div>
      {hasFooter && (
        <div className="flex flex-row items-center pt2 pt0-ns bn pl2-ns ml2-ns bl-ns hairline-1">
          {taxBehavior && <div className="f7 pr1">{taxBehavior}</div>}
          {model.nickname && <div className="fw5 f7">{model.nickname}</div>}
        </div>
      )}
    </div>
  ) : null;

  /**
   * TODO: share logic across pickers
   */
  const pickerTouched = get(touched, name);
  const pickerError = errors[name] || get(errors, name);
  const showError = pickerTouched && pickerError;
  let errorNodes;
  if (showError) {
    const fieldErrors = flattenErrorKeys(pickerError, name);
    errorNodes = fieldErrors.map((fieldError, errIx) => (
      <FieldError key={errIx} name={fieldError.path} />
    ));
  }
  const priceVal = getPricePickerPriceLabel(model);

  return (
    <Fragment>
      <div
        className={classnames("w-100 flex flex-column ba br2 ", {
          "b--red": showError,
          "hairline-1": !showError
        })}
      >
        <div className="flex flex-column flex-row-ns justify-between items-center-ns">
          <div
            className={classnames(
              "flex flex-row justify-between items-center w-100",
              {
                "bg-white--hover": editMode
              }
            )}
          >
            <div
              className={classnames("flex flex-row w-100 pa3", {
                pointer: editMode
              })}
              onClick={() => editMode && setShowOverlay(!showOverlay)}
            >
              <div
                className={classnames("fw5 f5 pr2 lh-title", {
                  "color-primary": !model
                })}
              >
                {priceVal}
              </div>
              <div className="dn flex-ns flex-row items-center">
                {activePriceInfo}
              </div>
            </div>
            <div className="pr3 flex flex-row items-center justify-end">
              {editMode && (
                <Fragment>
                  <div className="ph2">
                    <ConfirmIconButton
                      slides={PRODUCT_PRICE_REMOVAL}
                      onConfirm={() => {
                        arrayHelpers.remove(modelIx);
                        if (!get(touched, name)) {
                          setFieldTouched(name, true);
                        }
                      }}
                    />
                  </div>
                  <HoverIconButton
                    onClick={() => editMode && setShowOverlay(!showOverlay)}
                    tooltip={!showOverlay && "Show details"}
                    color={showOverlay ? COLORS.blue : COLORS.primary}
                    type={showOverlay ? "eye" : "eyeClosed"}
                  />
                </Fragment>
              )}
            </div>
          </div>
          <div
            onClick={() => editMode && setShowOverlay(!showOverlay)}
            className={classnames(
              "flex dn-ns flex-row justify-between items-center ph3 pb3",
              {
                "bb hairline-1": showOverlay
              }
            )}
          >
            <div className="flex flex-row">{activePriceInfo}</div>
          </div>
        </div>
      </div>
      {showOverlay && content && (
        <Overlay hideOverlay={() => setShowOverlay(false)}>
          <div className="pa3 pt5">
            <SectionBezel header="Price details">
              <Fragment>
                {content}
                <div className="flex flex-row w-100 justify-end pt3">
                  <Button
                    onClick={() => editMode && setShowOverlay(false)}
                    copy="Done"
                    theme="slim"
                  ></Button>
                </div>
              </Fragment>
            </SectionBezel>
          </div>
        </Overlay>
      )}
      {errorNodes}
    </Fragment>
  );
};

export default CheckoutPricePicker;
